import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { SendResultsView } from '../../components/sendResults/SendResultsView';
import { ApiError } from '../../lib/api';
import { canDeleteBroadcastFromList, deleteConfirmCopy } from '../../lib/broadcastPermissions';
import { useCurrentUser } from '../auth';
import { listAccounts } from '../accounts/api';
import { deleteBroadcast, duplicateBroadcast, getBroadcast, listBroadcastSends } from './api';
import { audienceCountLabel, brandLabel } from './audienceDescription';
import { broadcastToResultsData } from './broadcastResults';
import { ConfirmDeleteModal } from './ConfirmDeleteModal';

// Stage 6A §7 — per-broadcast results dashboard. Fetches the broadcast + its
// sends (polling every 10s while 'sending'), adapts to the normalized
// SendResultsData, and renders the shared <SendResultsView>. The report UI
// itself lives in that component (Stage 6A v5 refactor).

export function BroadcastDetail() {
  const { id } = useParams<{ id: string }>();
  const broadcast = useQuery({
    queryKey: ['broadcast', id],
    queryFn: () => getBroadcast(id!),
    enabled: Boolean(id),
    refetchInterval: (q) => (q.state.data?.status === 'sending' ? 10000 : false),
  });
  const sends = useQuery({
    queryKey: ['broadcast', id, 'sends'],
    queryFn: () => listBroadcastSends(id!),
    enabled: Boolean(id),
    refetchInterval: broadcast.data?.status === 'sending' ? 10000 : false,
  });
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const me = useCurrentUser();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const duplicate = useMutation({
    mutationFn: () => duplicateBroadcast(id!),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['broadcasts'] });
      navigate(`/broadcasts/${created.id}/edit`);
    },
  });
  const del = useMutation({
    mutationFn: () => deleteBroadcast(id!),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['broadcasts'] });
      navigate('/broadcasts', {
        state: { toast: res.kind === 'hard' ? 'Draft deleted.' : 'Broadcast deleted.' },
      });
    },
    onError: (err) => setDeleteError(err instanceof ApiError ? err.message : 'Delete failed.'),
  });

  if (broadcast.isLoading) return <CenteredSpinner />;
  if (broadcast.error || !broadcast.data) {
    return <p className="text-sm text-red-600">Couldn't load this broadcast.</p>;
  }

  const b = broadcast.data;
  const accountName = (accountId: string) =>
    accounts.data?.find((a) => a.id === accountId)?.name ?? accountId;
  const audienceDescription = `${brandLabel(b.audienceScope, accountName)} · ${audienceCountLabel(
    b.audienceScope,
  )} · ${b.channels.join(' + ')}`;

  const canDelete = canDeleteBroadcastFromList(b, me.uid, me.role);
  const copy = deleteConfirmCopy(b.status);

  const data = broadcastToResultsData(b, sends.data ?? [], {
    audienceDescription,
    duplicate: {
      onClick: () => duplicate.mutate(),
      pending: duplicate.isPending,
      errorText: duplicate.error ? "Couldn't duplicate this broadcast." : null,
    },
    deleteAction: canDelete
      ? {
          onClick: () => {
            setDeleteError(null);
            setConfirmDelete(true);
          },
          pending: del.isPending,
          errorText: deleteError,
        }
      : undefined,
  });

  return (
    <>
      <SendResultsView data={data} recipientsLoading={sends.isLoading} backHref="/broadcasts" />
      {confirmDelete && (
        <ConfirmDeleteModal
          title={copy.title}
          message={copy.message}
          confirmLabel={copy.confirmLabel}
          busy={del.isPending}
          errorText={deleteError}
          onCancel={() => {
            if (!del.isPending) setConfirmDelete(false);
          }}
          onConfirm={() => del.mutate()}
        />
      )}
    </>
  );
}
